import { PLANNING_SCREENSHOT_TABLES } from '../constants/constants';
import { isATableBox } from './boxes';

// Sentinels du back pour une image planning de section récursive (cf.
// RecursiveSectionsController) : capture en cours, ou dernière tentative en échec.
export const PENDING_SCREENSHOT = '__pending_screenshot__';
export const FAILED_SCREENSHOT = '__failed_screenshot__';

// Relecture d'un état « en cours » (cf. store refreshIncompletePlanningScreenshots) :
// toutes les 30 s pendant 5 min, la durée au-delà de laquelle le back tient la
// génération pour perdue et la relance (PlanningScreenshotCapture::PENDING_TTL).
export const PLANNING_STATUS_REREAD_DELAY = 30 * 1000;
export const PLANNING_STATUS_MAX_REREADS = 10;

export const isPlanningScreenshotTable = (tableName) =>
  PLANNING_SCREENSHOT_TABLES.includes((tableName || '').toUpperCase());

// Thème figé sur la box (couleurs) : une capture par thème côté back, donc un
// état par thème. Clés triées, comme l'empreinte du back.
const themeKey = (theme) =>
  theme ? JSON.stringify(Object.keys(theme).sort().map((key) => [key, theme[key]])) : '';

// État des captures d'un tableau planning (`screenshot_status` du GET), par
// tranche de lignes : `rows`, ou `rows_<id>` en découpé. Gardé hors des box,
// qui sont sauvegardées : c'est un état serveur, relu à chaque lecture.
export const planningStatusKey = ({ documentId, tableName, tableConfig, slice }) =>
  [
    documentId,
    (tableName || '').toUpperCase(),
    tableConfig?.orientation ?? '',
    themeKey(tableConfig?.theme),
    slice,
  ].join('|');

const boxSlice = (box) => {
  const identifiers = box.content?.identifiers;
  if (identifiers?.length) return `rows_${identifiers.join('_')}`;
  if (box.content?.recursiveRowId != null) return `rows_${box.content.recursiveRowId}`;
  return 'rows';
};

export const planningStatusOfBox = (statuses, box, currentDocumentId) =>
  statuses?.[
    planningStatusKey({
      documentId: box.proposal?.id ?? currentDocumentId,
      tableName: box.tableName,
      tableConfig: box.tableConfig,
      slice: boxSlice(box),
    })
  ];

// Entrées à mémoriser pour une réponse de tableau planning.
export const planningStatusEntries = ({ documentId, tableName, tableConfig, table }) =>
  Object.entries(table?.screenshot_status ?? {}).map(([slice, state]) => [
    planningStatusKey({ documentId, tableName, tableConfig, slice }),
    state,
  ]);

// "" = capture manquante ; " " = ligne fictive d'un tableau sans poste.
const isMissingImage = (value) => value === '';

// Images planning qui sortiraient vides d'un PDF : cellules image vides d'un
// tableau planning, images de section récursive en cours ou en échec.
export const countUnavailablePlanningImages = (boxes) =>
  (boxes ?? []).reduce((count, box) => {
    if (isATableBox(box.type) && isPlanningScreenshotTable(box.tableName)) {
      const imageKeys = (box.content?.columns ?? [])
        .filter((column) => column.type === 'image')
        .map((column) => column.key);
      const missing = (box.content?.data ?? []).reduce(
        (sum, row) => sum + imageKeys.filter((key) => isMissingImage(row[key])).length,
        0
      );
      return count + missing;
    }
    const src = box.content?.src;
    return src === PENDING_SCREENSHOT || src === FAILED_SCREENSHOT ? count + 1 : count;
  }, 0);
